import { inject, Injectable } from '@angular/core';
import { Route } from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BaseService } from '../../core/common/services/base.abstract.service';
import { ApiFragment } from '../../core/records/entities/api-fragment.class';
import { TabItem } from '../../elements/entities/tab-item.entity';
import { MediaHavenPlugin } from '../entities/mediahaven-plugins.entity';
import { ShPluginTabProvider } from '../entities/plugin-providers.entity';
import { PluginGuard } from '../guards/plugin.guard';

class NoTabItem implements ShPluginTabProvider {
    getTabItem(record: ApiFragment, plugin: MediaHavenPlugin): Observable<TabItem | null> {
        return of(null);
    }

    getTabRoute(plugin: MediaHavenPlugin): Route | null {
        return null;
    }
}

@Injectable({ providedIn: 'root' })
export class ShPluginTabsService extends BaseService {
    private readonly pluginGuard: PluginGuard = inject(PluginGuard);

    // #region TabItems
    private readonly PLUGIN_SERVICE_MAPPING: Record<MediaHavenPlugin, ShPluginTabProvider> = {
        [MediaHavenPlugin.ADVANCED_MEDIA_PLAYER]: new NoTabItem(),
        [MediaHavenPlugin.DOSSIER_TRANSFER]: new NoTabItem(),
        [MediaHavenPlugin.FULL_TEXT_SEARCH]: new NoTabItem(),
        [MediaHavenPlugin.METABASE]: new NoTabItem(),
        [MediaHavenPlugin.RESUMABLE_UPLOADS_MH]: new NoTabItem(),
        [MediaHavenPlugin.SEMANTIC_SEARCH]: new NoTabItem(),
        [MediaHavenPlugin.SIMILARITY_SEARCH]: new NoTabItem(),
        [MediaHavenPlugin.VECTOR_FIELD]: new NoTabItem(),
        [MediaHavenPlugin.GOVERNANCE_ARCHIVE_OBJECTS]: new NoTabItem(),
        [MediaHavenPlugin.GENERATIVE_METADATA_MISTRAL_AI]: new NoTabItem(),
        [MediaHavenPlugin.GENERATIVE_METADATA_OPEN_AI]: new NoTabItem(),
        [MediaHavenPlugin.FACIAL_RECOGNITION_DEEP_FACE]: new NoTabItem(),
        [MediaHavenPlugin.VERSION_MANAGEMENT]: new NoTabItem()
    };

    public getPluginTabs(record: ApiFragment): Observable<TabItem[]> {
        return combineLatest(
            Object.keys(MediaHavenPlugin).map(plugin => this.getPluginTab(record, plugin as MediaHavenPlugin))
        ).pipe(map(list => list.filter((x): x is TabItem => x !== null)));
    }

    private getPluginTab(record: ApiFragment, plugin: MediaHavenPlugin): Observable<TabItem | null> {
        const tabService: ShPluginTabProvider = this.PLUGIN_SERVICE_MAPPING[plugin];

        return this.checkPluginActive(plugin).pipe(
            switchMap(isActive => (isActive ? tabService.getTabItem(record, plugin) : of(null)))
        );
    }

    private checkPluginActive(plugin: MediaHavenPlugin): Observable<boolean> {
        return this.pluginGuard.checkPlugins([plugin]);
    }
    // #endregion

    // #region Tab routes
    public getPluginTabRoutes(): Route[] {
        return Object.keys(MediaHavenPlugin)
            .map(plugin => this.getPluginTabRoute(plugin as MediaHavenPlugin))
            .filter((x): x is Route => x !== null);
    }

    private getPluginTabRoute(plugin: MediaHavenPlugin): Route | null {
        const tabService: ShPluginTabProvider = this.PLUGIN_SERVICE_MAPPING[plugin];
        return tabService.getTabRoute(plugin);
    }
    // #endregion
}
